// DiaryHero — detail-page hero card (W32 polish).
//
// Mirrors AnniversaryHero (W3.1 §5.2): icon chip + title + meta row
// (date / weekday / author chip / mood) + privacy indicator + relative
// "今天" / "3 天前" badge.  No more orphan "由你寫的" pill at top-right —
// the author attribution is now row-aligned with the date so the eyebrow
// stays flat (no overflow into the PageHero hairline-b).

import { BookHeart, Lock } from "lucide-react";

import { DIARY_I18N } from "@/lib/i18n/diary";
import { formatAbsoluteZh, formatRelative, parseDateOnly } from "@/lib/utils/date";
import type { DiaryOut } from "@/lib/api/diary";
import { useAuth } from "@/lib/auth/AuthProvider";
import { cn } from "@/lib/utils/cn";
// W42: this hero was a hand-rolled `<article>` that took the slot's fill and
// mascot but not `position: relative`, and the mascot landed on the topbar
// avatar. It now renders through `<HeroCard as="article">`, so the mascot is
// inside a positioned container by construction — the F17 check runs there too,
// rather than being the one screen where the rule is unenforced.
import { HeroCard } from "@/components/primitives/HeroCard";
import { HERO_DIARIES } from "@/lib/screenType";

export interface DiaryHeroProps {
  diary: DiaryOut;
}

export function DiaryHero({ diary }: DiaryHeroProps) {
  const { user } = useAuth();
  const isAuthor = user?.id === diary.author_id;
  const moodEmoji = diary.mood ? DIARY_I18N.mood_emoji(diary.mood) : null;
  const moodLabel = diary.mood ? DIARY_I18N.mood_label(diary.mood) : null;
  const dateObj = parseDateOnly(diary.date);
  const absoluteDate = formatAbsoluteZh(dateObj);
  const relative = formatRelative(new Date(diary.created_at));

  return (
    // W42: this **is** the diaries peak card — the slot's fill and the slot's
    // mascot both live on it — so it renders through `<HeroCard as="article">`
    // rather than a hand-rolled `<article>`.
    //
    // The hand-rolled version was a real bug, and an instructive one. It carried
    // the fill, the mascot and `data-peak-card`, but not `position: relative`,
    // so the absolutely-positioned mascot resolved against the **page** and landed
    // on the topbar avatar — found by looking at a screenshot, not by any test.
    // Swapping the element is the fix: the mascot is now inside a positioned
    // container by construction, so there is no fourth place to forget it.
    <HeroCard
      as="article"
      slot={HERO_DIARIES}
      className={cn(
        // W53.2 `ADR-0180` — the radius is `rounded-lg` 16px, which is what
        // `HeroCard` already paints, so this override is now a restatement
        // rather than a second value. It was `rounded-xl`, and stating it
        // explicitly is what let the hero and the L2 card disagree about what
        // 12px even means here: `rounded-xl` is Tailwind's *default* 12px, a
        // value that happens to equal `--r-md` under a name that is not in the
        // scale — so nothing in `globals.css` or `hard-rule-audit.mjs` could
        // ever have flagged it.
        "rounded-lg p-5 md:p-6 flex flex-col gap-4",
        diary.is_private && "ring-1 ring-partner-tint",
      )}
      testId="diary-hero"
      dataAnchor={diary.is_private}
    >
      <header className="flex items-start gap-3">
        <span
          aria-hidden
          className={cn(
            "inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-full",
            diary.is_private
              ? "bg-partner-tint text-partner-strong"
              : "bg-you-tint text-you-strong",
          )}
        >
          {diary.is_private ? (
            <Lock size={22} strokeWidth={1.5} />
          ) : (
            <BookHeart size={22} strokeWidth={1.5} />
          )}
        </span>
        <div className="flex-1 min-w-0 flex flex-col gap-s-2">
          <p data-testid="diary-hero-date" className="text-body-sm text-ink-1/80 tabular-nums">
            <span>{absoluteDate}</span>
            <span aria-hidden> · </span>
            <span className="text-ink-1/70">{relative}</span>
            {diary.is_private ? (
              <>
                <span aria-hidden> · </span>
                <span
                  className="rounded-pill bg-partner-tint px-2 py-0.5 text-meta font-semibold text-partner-strong"
                  data-testid="diary-hero-private-pill"
                >
                  私人
                </span>
              </>
            ) : null}
          </p>
          {diary.title ? (
            <h1
              className="font-display text-display-detail text-ink-1 leading-tight"
              data-testid="diary-hero-title"
            >
              {diary.title}
            </h1>
          ) : null}
          <p className="text-body-sm text-ink-1/70 flex items-center gap-2">
            <span
              className={cn(
                "rounded-pill px-2 py-0.5 text-meta font-semibold",
                isAuthor ? "bg-you-tint text-you-strong" : "bg-partner-tint text-partner-strong",
              )}
              data-testid="diary-hero-author"
            >
              {isAuthor ? "由你寫的" : "由對方寫的"}
            </span>
            {moodEmoji ? (
              <span className="inline-flex items-center gap-1 text-ink-1/80">
                <span aria-hidden className="text-title-3">
                  {moodEmoji}
                </span>
                {moodLabel ? <span className="text-meta text-ink-1/70">{moodLabel}</span> : null}
              </span>
            ) : null}
          </p>
        </div>
      </header>

      {/* Privacy banner for non-author viewing a private diary. */}
      {diary.is_private ? (
        <div
          data-testid="diary-hero-private-note"
          className="bg-partner-tint rounded-lg p-4 text-body-sm text-ink-1"
        >
          這篇日記是私人日記, 只有作者能讀取。
        </div>
      ) : null}
    </HeroCard>
  );
}
